import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronRight, Plus } from "lucide-react";
import { PageHeader, inr } from "@/components/platform/platformUi";

const emptyCreate = {
    business_name: "",
    full_name: "",
    email: "",
    password: "",
    mobile: "",
    city: "",
    business_type: "salon",
    trial_days: "14",
};

export default function PlatformTenants() {
    const [tenants, setTenants] = useState([]);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [trial, setTrial] = useState("");
    const [busy, setBusy] = useState(false);
    const [extendDays, setExtendDays] = useState({});
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState(emptyCreate);

    const loadTenants = async (params) => {
        try {
            const r = await api.get("/platform/tenants", { params });
            setTenants(r.data || []);
        } catch {
            toast.error("Could not load salons");
        }
    };

    useEffect(() => {
        loadTenants({ q, status, trial });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, status, trial]);

    const refresh = () => loadTenants({ q, status, trial });

    const toggleTenantStatus = async (t) => {
        const next = t.status === "suspended" ? "active" : "suspended";
        setBusy(true);
        try {
            await api.patch(`/platform/tenants/${t.id}`, { status: next });
            toast.success(`Salon ${next === "active" ? "resumed" : "suspended"}`);
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    const extendTrial = async (t) => {
        const days = Number(extendDays[t.id] || 0);
        if (!days || days < 1) {
            toast.error("Enter extend days (1+)");
            return;
        }
        setBusy(true);
        try {
            await api.patch(`/platform/tenants/${t.id}`, { extend_trial_days: days });
            toast.success("Trial extended");
            setExtendDays((prev) => ({ ...prev, [t.id]: "" }));
            refresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Operation failed");
        } finally {
            setBusy(false);
        }
    };

    const sortedTenants = useMemo(() => tenants, [tenants]);

    const createSalon = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/platform/tenants", {
                ...createForm,
                trial_days: Number(createForm.trial_days) || 14,
            });
            toast.success(`Salon “${r.data.name}” created`);
            setShowCreate(false);
            setCreateForm(emptyCreate);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create salon");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-salons-page">
            <PageHeader
                eyebrow="Platform admin"
                title="Salons"
                subtitle={`${sortedTenants.length} tenant${sortedTenants.length === 1 ? "" : "s"} on the platform`}
                action={
                    <Button
                        type="button"
                        onClick={() => setShowCreate(true)}
                        className="bg-[#4A0E17] text-white hover:bg-[#3a0b12]"
                    >
                        <Plus className="mr-1.5 size-4" />
                        Create salon
                    </Button>
                }
            />

            {showCreate && (
                <form
                    onSubmit={createSalon}
                    className="space-y-4 rounded-xl border border-[#E8E4DE] bg-white p-5 shadow-[0_4px_22px_rgba(45,41,38,0.05)]"
                >
                    <p className="text-sm font-bold text-[#4A0E17]">Provision new salon</p>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Business name</Label>
                            <Input value={createForm.business_name} onChange={(e) => setCreateForm((f) => ({ ...f, business_name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Owner name</Label>
                            <Input value={createForm.full_name} onChange={(e) => setCreateForm((f) => ({ ...f, full_name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Owner email</Label>
                            <Input type="email" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Password</Label>
                            <Input type="password" value={createForm.password} onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))} required minLength={8} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Mobile</Label>
                            <Input value={createForm.mobile} onChange={(e) => setCreateForm((f) => ({ ...f, mobile: e.target.value }))} required />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">City</Label>
                            <Input value={createForm.city} onChange={(e) => setCreateForm((f) => ({ ...f, city: e.target.value }))} />
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Type</Label>
                            <select className="h-9 w-full rounded-md border border-[#E8E4DE] px-3 text-sm" value={createForm.business_type} onChange={(e) => setCreateForm((f) => ({ ...f, business_type: e.target.value }))}>
                                <option value="salon">Salon</option>
                                <option value="spa">Spa</option>
                                <option value="both">Both</option>
                            </select>
                        </div>
                        <div className="space-y-1">
                            <Label className="text-[11px] font-bold uppercase text-[#3D2817]">Trial days</Label>
                            <Input type="number" min={0} max={90} value={createForm.trial_days} onChange={(e) => setCreateForm((f) => ({ ...f, trial_days: e.target.value }))} />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Button type="submit" disabled={busy} className="bg-[#4A0E17] text-white">Create salon</Button>
                        <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                    </div>
                </form>
            )}

            <section className="space-y-3">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">
                            Search
                        </Label>
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="h-9 w-44"
                            placeholder="name / city / slug"
                        />
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">
                            Status
                        </Label>
                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm font-medium text-[#3D2817]"
                        >
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="suspended">Suspended</option>
                        </select>
                    </div>
                    <div className="space-y-1">
                        <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#3D2817]">
                            Trial
                        </Label>
                        <select
                            value={trial}
                            onChange={(e) => setTrial(e.target.value)}
                            className="h-9 rounded-md border border-[#E8E4DE] bg-white px-3 text-sm font-medium text-[#3D2817]"
                        >
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                    <table className="min-w-max w-full border-collapse">
                        <thead>
                            <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                                <th className="px-4 py-3">Salon</th>
                                <th className="px-4 py-3">Owner</th>
                                <th className="px-4 py-3">Plan</th>
                                <th className="px-4 py-3">Subscription</th>
                                <th className="px-4 py-3">Customers</th>
                                <th className="px-4 py-3">Revenue</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Trial</th>
                                <th className="px-4 py-3">Extend</th>
                                <th className="px-4 py-3">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTenants.map((t) => (
                                <tr key={t.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                    <td className="px-4 py-3">
                                        <Link
                                            to={`/platform/salons/${t.id}`}
                                            className="group flex items-center gap-1 font-semibold text-[#4A0E17] hover:text-[#C9A227]"
                                        >
                                            {t.name}
                                            <ChevronRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                                        </Link>
                                        <div className="text-xs font-medium text-[#8A8178]">
                                            {[t.city, t.slug].filter(Boolean).join(" · ")}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium text-[#3D2817]">
                                        {t.owner?.email || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">
                                        {t.plan?.name || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-sm capitalize font-semibold text-[#3D2817]">
                                        {t.subscription_status || "trial"}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">
                                        {t.stats?.customers ?? 0}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">
                                        {inr(t.stats?.lifetime_revenue)}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold">
                                        <span
                                            className={`rounded-full px-2.5 py-0.5 ${
                                                t.status === "suspended"
                                                    ? "bg-[#FEF5F3] text-[#C84B31]"
                                                    : "bg-[#FDF3DC] text-[#9A7B10]"
                                            }`}
                                        >
                                            {t.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold text-[#3D2817]">
                                        {t.trial_status || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                min={1}
                                                className="h-9 w-20"
                                                value={extendDays[t.id] || ""}
                                                onChange={(e) =>
                                                    setExtendDays((p) => ({ ...p, [t.id]: e.target.value }))
                                                }
                                                placeholder="days"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className="h-9 border-[#E8E4DE] bg-white px-2 text-[11px] font-semibold text-[#3D2817] hover:border-[#D4AF37]/45"
                                                disabled={busy}
                                                onClick={() => extendTrial(t)}
                                            >
                                                Extend
                                            </Button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="h-9 border-[#E8E4DE] bg-white px-2 text-[11px] font-semibold text-[#3D2817] hover:border-[#D4AF37]/45"
                                            disabled={busy}
                                            onClick={() => toggleTenantStatus(t)}
                                        >
                                            {t.status === "suspended" ? "Resume" : "Suspend"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {sortedTenants.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={10}
                                        className="px-4 py-10 text-center text-sm font-medium text-[#8A8178]"
                                    >
                                        No salons found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
}
